import React, { useMemo, useRef, useState } from 'react'
import {
  Box,
  List,
  Drawer,
  Button,
  Divider,
  ListItem,
  Typography,
  ListItemText,
  ListItemButton,
  Paper,
  Popper,
} from '@mui/material'

import { useTheme } from '@mui/material/styles'
import { TENANTS, TENANT_TOPBAR_CONFIGS } from '@/constants/tenant'
import {
  TopbarRoot,
  Container,
  LogoImg,
  DesktopNav,
  NavList,
  NavLink,
  Actions,
  BurgerBtn,
} from './styled'

export default function TenantTopbar({
  tenant = 'suncoast',
  isAuthenticated = false,
}) {
  const theme = useTheme()

  const config = useMemo(() => {
    const base =
      TENANT_TOPBAR_CONFIGS[tenant] || TENANT_TOPBAR_CONFIGS[TENANTS.SUNCOAST]
    const actions = [
      ...(base.actions || []),
      ...(!isAuthenticated ? base.actionsGuest || [] : []),
    ]
    return { ...base, actions }
  }, [tenant, isAuthenticated])

  const [mobileOpen, setMobileOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)
  const [openMenuKey, setOpenMenuKey] = useState(null)
  const closeTimerRef = useRef(null)

  const closeNow = () => {
    setAnchorEl(null)
    setOpenMenuKey(null)
  }

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
  }

  const startCloseTimer = () => {
    clearCloseTimer()
    closeTimerRef.current = setTimeout(() => {
      closeNow()
    }, 120)
  }

  return (
    <TopbarRoot data-tenant={tenant}>
      {config.preHeader && (
        <Box
          sx={{
            bgcolor: theme.palette.background.default,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Container>{renderPreHeader(config)}</Container>
        </Box>
      )}
      <Container>
        <a href={config.logo.href}>
          <LogoImg
            src={config.logo.src}
            alt={config.logo.alt || 'Logo'}
            style={{ maxWidth: config.logo.maxWidth ?? 200 }}
          />
        </a>

        <DesktopNav aria-label='Main'>
          <NavList>
            {config.menus.map(menu => {
              const hasSubmenu = !!(menu.links && menu.links.length > 0)
              const isOpen = openMenuKey === menu.title
              const handleEnter = e => {
                if (!hasSubmenu) return
                clearCloseTimer()
                setAnchorEl(e.currentTarget)
                setOpenMenuKey(menu.title)
              }
              const handleTriggerLeave = () => {
                if (!hasSubmenu) return
                startCloseTimer()
              }
              return (
                <li key={menu.title}>
                  {menu.url && !hasSubmenu ? (
                    <NavLink href={menu.url} onMouseEnter={handleEnter}>
                      {menu.title}
                    </NavLink>
                  ) : (
                    <NavLink
                      as='span'
                      onMouseEnter={handleEnter}
                      onMouseLeave={handleTriggerLeave}
                    >
                      {menu.title}
                    </NavLink>
                  )}
                  {hasSubmenu && (
                    <Popper
                      open={isOpen}
                      anchorEl={anchorEl}
                      placement='bottom-start'
                      modifiers={[
                        { name: 'offset', options: { offset: [0, 8] } },
                      ]}
                    >
                      <Paper
                        elevation={3}
                        onMouseEnter={clearCloseTimer}
                        onMouseLeave={closeNow}
                        sx={{ p: 1, minWidth: 260 }}
                      >
                        <List dense disablePadding>
                          {menu.links.map(link => (
                            <ListItem key={link.title} disablePadding>
                              <ListItemButton component='a' href={link.url}>
                                <ListItemText primary={link.title} />
                              </ListItemButton>
                            </ListItem>
                          ))}
                        </List>
                      </Paper>
                    </Popper>
                  )}
                </li>
              )
            })}
            {config.trailing &&
              config.trailing.map((node, index) => (
                <li key={`trailing-${index}`}>{renderTrailing(node)}</li>
              ))}
          </NavList>
        </DesktopNav>

        <Actions>
          {config.actions?.map(action => (
            <Button
              key={action.title}
              variant='outlined'
              color='primary'
              href={action.href}
            >
              {action.title}
            </Button>
          ))}
        </Actions>

        <BurgerBtn
          aria-label='Toggle navigation'
          onClick={() => setMobileOpen(v => !v)}
        >
          <span />
          <span />
          <span />
        </BurgerBtn>
      </Container>

      <Drawer
        anchor='top'
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
      >
        <Box role='presentation' sx={{ bgcolor: 'background.paper' }}>
          {config.preHeader && (
            <Box sx={{ px: 2, py: 1 }}>{renderPreHeader(config)}</Box>
          )}
          <Divider />
          <List>
            {config.menus.map(menu => (
              <React.Fragment key={menu.title}>
                <ListItem disablePadding>
                  {menu.url ? (
                    <ListItemButton component='a' href={menu.url}>
                      <ListItemText primary={menu.title} />
                    </ListItemButton>
                  ) : (
                    <ListItemText primary={menu.title} sx={{ px: 2, py: 1 }} />
                  )}
                </ListItem>
                {menu.links && menu.links.length > 0 && (
                  <List sx={{ pl: 4 }}>
                    {menu.links.map(link => (
                      <ListItem key={link.title} disablePadding>
                        <ListItemButton component='a' href={link.url}>
                          <ListItemText primary={`- ${link.title}`} />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                )}
              </React.Fragment>
            ))}
          </List>
          <Divider />
          <Box sx={{ display: 'flex', gap: 1, px: 2, py: 1 }}>
            {config.actions?.map(action => (
              <Button
                key={action.title}
                variant='outlined'
                color='primary'
                href={action.href}
              >
                {action.title}
              </Button>
            ))}
          </Box>
        </Box>
      </Drawer>
    </TopbarRoot>
  )
}

function renderPreHeader(config) {
  if (!config.preHeader) return null
  const { text, socials } = config.preHeader
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
      }}
    >
      <Typography variant='caption' color='text.secondary'>
        {text}
      </Typography>
      <Box sx={{ display: 'flex', gap: 2 }}>
        {(socials || []).map(s => (
          <Button
            key={s.title}
            href={s.url}
            target='_blank'
            rel='noreferrer'
            size='small'
          >
            {s.title}
          </Button>
        ))}
      </Box>
    </Box>
  )
}

function renderTrailing(node) {
  if (!node) return null
  if (typeof node === 'string') {
    return <Typography variant='body2'>{node}</Typography>
  }
  if (node.url && node.title) {
    return (
      <Button href={node.url} variant='text' color='primary'>
        {node.title}
      </Button>
    )
  }
  return null
}
